import { ChangeDetectionStrategy, Component, effect, EventEmitter, input, Output, viewChild } from '@angular/core';
import { NgbDate, NgbDateParserFormatter, NgbDatepickerI18n, NgbDatepickerModule, NgbDateStruct, NgbInputDatepicker } from '@ng-bootstrap/ng-bootstrap';
import { CustomDateParserFormatter } from './date-parser-formatter';
import { DateRange } from './date-range';
import { CustomDatepickerI18n } from './datepicker-i18n';


/**
 * Date range picker component.
 */
@Component({
	selector: 'app-daterangepicker',
	imports: [
		NgbDatepickerModule
	],
	providers: [
		{ provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter },
		{ provide: NgbDatepickerI18n, useClass: CustomDatepickerI18n }
	],
	templateUrl: './daterangepicker.component.html',
	changeDetection: ChangeDetectionStrategy.Eager
})
export class DaterangepickerComponent {

	placement = input('');
	positionTarget = input<string | HTMLElement>('');
	range = input<DateRange | null>(null);

	@Output() rangeChanged = new EventEmitter<DateRange | null>();

	inputDatePicker = viewChild(NgbInputDatepicker);

	hoveredDate: NgbDate | null = null;

	fromDate: NgbDate | null = null;
	toDate: NgbDate | null = null;

	MIN_DATE: NgbDateStruct = { year: 1900, month: 1, day: 1 };
	MAX_DATE: NgbDateStruct = { year: 2300, month: 1, day: 1 };

	constructor() {
		effect(() => this._range = this.range());
	}

	/**
	 * Toggle the calendar.
	 */
	toggle(): void {
		if (!this.fromDate || this.toDate) {
			this.fromDate = null;
			this.toDate = null;
		}
		this.inputDatePicker()?.toggle();
	}

	private set _range(val: DateRange | null) {
		this.fromDate = val ? new NgbDate(val.from.getFullYear(), val.from.getMonth() + 1, val.from.getDate()) : null;
		this.toDate = val ? new NgbDate(val.to.getFullYear(), val.to.getMonth() + 1, val.to.getDate()) : null;
	}

	/**
	 * Update and emit date range selected.
	 */
	onDateSelection(date: NgbDate): void {
		if (!this.fromDate && this.toDate) {
			this.fromDate = date;
		} else if (this.fromDate && !this.toDate && date && (date.after(this.fromDate) || date.equals(this.fromDate))) {
			this.toDate = date;
			this._emitDateRange();
		} else {
			this.toDate = null;
			this.fromDate = date;
			this._emitDateRange();
		}
	}

	private _emitDateRange(): void {
		if (this.fromDate && this.toDate) {
			const from = new Date(this.fromDate.year, this.fromDate.month - 1, this.fromDate.day, 0, 0, 0, 0);
			const to = new Date(this.toDate.year, this.toDate.month - 1, this.toDate.day, 23, 59, 59, 999);
			this.rangeChanged.emit({ from: from, to: to });
		} else {
			this.rangeChanged.emit(null);
		}
	}

	/**
	 * Check if is selecting the second date.
	 */
	isHovered(date: NgbDate): boolean {
		return !!this.fromDate && !this.toDate && !!this.hoveredDate && date.after(this.fromDate) && date.before(this.hoveredDate);
	}

	/**
	 * Check if date is between range selected.
	 */
	isInside(date: NgbDate): boolean {
		return !!this.toDate && date.after(this.fromDate) && date.before(this.toDate);
	}

	/**
	 * Check if range selected is correct.
	 */
	isRange(date: NgbDate): boolean {
		return date.equals(this.fromDate) || (this.toDate && date.equals(this.toDate)) || this.isInside(date) || this.isHovered(date);
	}
}
